import React, { useState, useEffect, useCallback } from 'react';
import { IonButton, IonToast } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AuthService from '../../services/auth';
import ProjectsService from '../../services/projects';
import Marketplace from '../../services/marketplace';
import { useProjects } from '../../hooks/useProjects';
import { queryKeys } from '../../hooks/queryKeys';
import ProjectsList from './ProjectsList';
import ProjectModal from '../../components/organisms/Modals/ProjectModal/ProjectModal';
import ProjectPickerForDeployModal from '../../components/organisms/Modals/ProjectPickerForDeployModal';
import PageLayout from '../../components/layouts/PageLayout';
import PageHeader from '../../components/atoms/PageHeader/PageHeader';
import ConfirmModal from '../../components/molecules/ConfirmModal/ConfirmModal';
import './ProjectsPage.css';

/**
 * ProjectsPage — projects list + create / edit modal.
 *
 * Deploy-from-marketplace handoff:
 *   /projects?deployTemplate=<slug> means the user arrived from the public
 *   marketplace clicking Deploy. We resolve the template via
 *   Marketplace.getWorkflow, then either:
 *     - open the project picker (1+ projects) so they can choose where to
 *       deploy, OR
 *     - open the create-project modal in "create & deploy" mode (0 projects)
 *       so a brand-new user can do both in a single form.
 *
 *   On modal cancellation we clear the URL param so refreshing doesn't
 *   re-open the flow with stale intent.
 */
const ProjectsPage = ({ pageRef }) => {
  const queryClient = useQueryClient();
  const [, setUser] = useState(null);
  // React-Query cached list. `isLoading` is true ONLY on the first load (no
  // cached data yet), so the skeleton no longer flashes on every revisit — a
  // cached list paints instantly and revalidates in the background.
  const { data: projects = [], isLoading: loading, refetch: refetchProjects } = useProjects();
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', color: '' });

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deployTemplateSlug = searchParams.get('deployTemplate');

  // Hydrated template metadata (name, icon) for the picker / create-and-
  // deploy modals. Null until the GET resolves.
  const [deployTemplate, setDeployTemplate] = useState(null);
  // blockedDelete: set to the project row when Delete is clicked but the
  // project still has workflows / apps. Shows an explanatory modal instead
  // of disabling the button — the user gets a clear message and a path forward.
  const [blockedDelete, setBlockedDelete] = useState(null);
  // Picker is the right surface when there's 1+ existing projects;
  // ProjectModal (in deploy mode) covers the 0-projects case.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployingProjectId, setDeployingProjectId] = useState(null);

  // Show toast notification
  const showToast = (message, color) => {
    setToast({ show: true, message, color });
  };

  // Refresh the cached list (used after create / update / delete). useProjects
  // owns the actual fetch + loading state; this just triggers a revalidation.
  const fetchProjects = useCallback(() => refetchProjects(), [refetchProjects]);

  // Get user info (the project list is fetched + cached by useProjects).
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      setUser(AuthService.getCurrentUser());
    }
  }, []);

  // ── deployTemplate handshake ─────────────────────────────────────────
  // 1) Resolve the template metadata once we know the slug.
  useEffect(() => {
    if (!deployTemplateSlug) {
      setDeployTemplate(null);
      return;
    }
    let cancelled = false;
    Marketplace.getWorkflow(deployTemplateSlug)
      .then((tpl) => {
        if (!cancelled) setDeployTemplate(tpl);
      })
      .catch((err) => {
        console.error('Failed to resolve template for deploy handoff:', err);
        if (!cancelled) {
          showToast(`Couldn't find template "${deployTemplateSlug}"`, 'danger');
          // Strip the bad param so we don't loop on refresh.
          const next = new URLSearchParams(searchParams);
          next.delete('deployTemplate');
          setSearchParams(next, { replace: true });
        }
      });
    return () => { cancelled = true; };
  }, [deployTemplateSlug, searchParams, setSearchParams]);

  // 2) Once the template AND the project list are both ready, open the
  // right surface. Don't re-open if the user already dismissed once
  // (modalOpen / pickerOpen guards that).
  useEffect(() => {
    if (!deployTemplate || loading) return;
    if (modalOpen || pickerOpen) return;
    if (projects.length === 0) {
      // 0 projects → fold project creation + deploy into one form.
      setEditingProject(null);
      setModalOpen(true);
    } else {
      setPickerOpen(true);
    }
  }, [deployTemplate, loading, projects.length, modalOpen, pickerOpen]);

  // Clear ?deployTemplate= from the URL when the user opts out of the
  // flow. Keeps the address bar honest and prevents the auto-open
  // useEffect from re-firing after a manual close.
  const clearDeployIntent = useCallback(() => {
    if (!deployTemplateSlug) return;
    const next = new URLSearchParams(searchParams);
    next.delete('deployTemplate');
    setSearchParams(next, { replace: true });
    setDeployTemplate(null);
  }, [deployTemplateSlug, searchParams, setSearchParams]);

  // ── modal lifecycle ──────────────────────────────────────────────────
  // Open modal for creating new project
  const handleAddProject = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingProject(null);
    // Closing the create-and-deploy modal counts as opting out of the
    // deploy intent — without this the auto-open useEffect would re-open
    // the modal on the next render.
    if (deployTemplate && !editingProject) {
      clearDeployIntent();
    }
  };

  // ── deploy primitive (shared by picker + create-and-deploy) ─────────
  const deployToProject = useCallback(async (projectId) => {
    if (!deployTemplate?.slug || !projectId) return;
    setDeploying(true);
    setDeployingProjectId(projectId);
    try {
      const res = await Marketplace.deployWorkflow(deployTemplate.slug, projectId, {});
      const deployedAs = res?.workflowType || deployTemplate.slug;
      if (res?.alreadyInstalled) {
        showToast(`${deployTemplate.name || deployTemplate.slug} is already deployed there`, 'warning');
      } else {
        showToast(`Deployed ${deployTemplate.name || deployTemplate.slug}`, 'success');
      }
      // Clear UI state + URL intent before navigating.
      setPickerOpen(false);
      setModalOpen(false);
      clearDeployIntent();
      navigate(`/projects/${projectId}/agents/${encodeURIComponent(deployedAs)}`);
    } catch (err) {
      console.error('Deploy failed:', err);
      showToast(`Deploy failed: ${err?.message || 'unknown error'}`, 'danger');
    } finally {
      setDeploying(false);
      setDeployingProjectId(null);
    }
  }, [deployTemplate, navigate, clearDeployIntent]);

  // Handle modal submission (create or update)
  const handleModalSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingProject) {
        await ProjectsService.updateProject(editingProject.projectId, data);
        showToast('Project updated successfully', 'success');
        setModalOpen(false);
        setEditingProject(null);
        fetchProjects();
      } else {
        // Create new project. In deploy mode we chain a deploy + navigate;
        // otherwise the original behaviour (copy token, refresh list).
        const response = await ProjectsService.createProject(data);
        const newProject = response?.project;
        const apiToken = response?.apiToken;

        if (deployTemplate && newProject?.projectId) {
          // Refresh the list in the background so when the user comes
          // back to /projects they see the new row, but don't await it
          // (the deploy + navigate is the hot path).
          fetchProjects();
          setModalOpen(false);
          await deployToProject(newProject.projectId);
          // deployToProject handles toast + navigate; nothing more to do.
        } else {
          if (apiToken) {
            navigator.clipboard.writeText(apiToken).catch(() => {});
          }
          showToast(apiToken ? 'Project created — its API key is copied to your clipboard (it will not be shown again)' : 'Project created successfully', 'success');
          setModalOpen(false);
          fetchProjects();
        }
      }
    } catch (error) {
      console.error('Error saving project:', error);
      showToast('Failed to save project', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete button click.
  // If the project still has workflows or apps, show a blocking modal that
  // explains what needs to be removed first instead of silently disabling
  // the button.
  // Otherwise fall through to a confirm dialog then optimistic deletion.
  const handleDelete = async (project) => {
    const c = project?._counts;
    const hasWorkflows = c && Number(c.workflows || 0) > 0;
    const hasApps = c && Number(c.apps || 0) > 0;
    if (hasWorkflows || hasApps) {
      setBlockedDelete(project);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
      return;
    }

    const listKey = queryKeys.projects.list();
    const prevProjects = queryClient.getQueryData(listKey);
    // Optimistic removal from the cache; roll back on failure.
    queryClient.setQueryData(listKey, (cur) =>
      Array.isArray(cur) ? cur.filter((p) => p.projectId !== project.projectId) : cur);
    try {
      await ProjectsService.deleteProject(project.projectId);
      showToast('Project deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting project:', error);
      if (prevProjects !== undefined) queryClient.setQueryData(listKey, prevProjects);
      const msg = error?.body?.error || error?.message || 'Failed to delete project';
      showToast(msg, 'danger');
    }
  };

  // A project's API token is shown once — when it is created or rotated. The
  // list only knows whether one is set and its last 4 characters, so the row
  // action issues a NEW token (owner only), copies it, and warns that the old
  // one stops working.
  const [rotateTarget, setRotateTarget] = useState(null);
  const handleRotateApiKey = (project) => {
    if (!project.isOwner) {
      showToast('Only the project owner can issue a new API key', 'warning');
      return;
    }
    setRotateTarget(project);
  };
  const rotateApiKey = async () => {
    const project = rotateTarget;
    try {
      const response = await ProjectsService.updateProject(project.projectId, { rotateApiToken: true });
      const token = response?.apiToken;
      if (!token) throw new Error('No key came back');
      await navigator.clipboard.writeText(token).catch(() => {});
      showToast('New API key copied to clipboard — the old key no longer works', 'success');
      fetchProjects();
    } catch (error) {
      console.error('Error issuing a new API key:', error);
      showToast('Failed to issue a new API key', 'danger');
    }
  };

  return (
    <>
      <Helmet>
        <title>Projects - Zibby</title>
        <meta name="description" content="Manage your Zibby projects and automation workflows." />
      </Helmet>
      <PageLayout
      breadcrumbs={[{ label: 'Projects' }]}
      actions={
        projects.length > 0 && (
          <IonButton
            className="header-action-btn"
            color="primary"
            onClick={handleAddProject}
          >
            Add
          </IonButton>
        )
      }
    >
      <PageHeader subtitle="Project provides a workspace for organizing tickets, test cases, and integrations." />

      {/* Projects List */}
      <ProjectsList
        projects={projects}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopyApiKey={handleRotateApiKey}
        onAdd={handleAddProject}
      />

      {/* Project Modal — doubles as the create-and-deploy form when
          deployTemplate is set (only meaningful for create, not edit). */}
      <ProjectModal
        isOpen={modalOpen}
        onDismiss={handleModalClose}
        onSubmit={handleModalSubmit}
        initialProject={editingProject}
        isSubmitting={submitting || (!editingProject && deploying)}
        deployTemplate={!editingProject ? deployTemplate : null}
      />

      {/* Project Picker — shown when the user arrived with a deploy
          intent AND already has at least one project. */}
      <ProjectPickerForDeployModal
        isOpen={pickerOpen}
        onDismiss={() => {
          setPickerOpen(false);
          clearDeployIntent();
        }}
        projects={projects}
        templateName={deployTemplate?.name || deployTemplate?.slug}
        templateIcon={deployTemplate?.icon}
        onSelectProject={(pid) => deployToProject(pid)}
        onCreateNewProject={() => {
          setPickerOpen(false);
          setEditingProject(null);
          setModalOpen(true);
        }}
        deploying={deploying}
        deployingProjectId={deployingProjectId}
      />

      {/* Blocked-delete modal — shown when user clicks Delete on a project
          that still has workflows or apps. Explains what to remove first
          instead of silently disabling the button. */}
      {blockedDelete && (() => {
        const c = blockedDelete._counts || {};
        const parts = [];
        if (Number(c.workflows || 0) > 0) parts.push(`${c.workflows} pipeline${c.workflows === 1 ? '' : 's'}`);
        if (Number(c.apps || 0) > 0) parts.push(`${c.apps} app${c.apps === 1 ? '' : 's'}`);
        return (
          <ConfirmModal
            open
            onClose={() => setBlockedDelete(null)}
            title={`Can't delete "${blockedDelete.name}"`}
            ariaLabel="Cannot delete project"
            secondaryLabel="Got it"
          >
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
              This project still has <strong style={{ color: '#fff' }}>{parts.join(' and ')}</strong>.
              Delete {parts.length > 1 ? 'them' : 'it'} first, then come back to delete the project.
            </p>
          </ConfirmModal>
        );
      })()}

      {rotateTarget && (
        <ConfirmModal
          open
          onClose={() => setRotateTarget(null)}
          title={`New API key for "${rotateTarget.name}"`}
          ariaLabel="Issue a new API key"
          primaryLabel="Issue new key"
          onPrimary={rotateApiKey}
          secondaryLabel="Cancel"
        >
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
            A project&apos;s API key is only shown when it is issued. This makes a new one and copies it to
            your clipboard. <strong style={{ color: '#fff' }}>The current key stops working</strong> — update
            anything that uses it (CI, scripts), and agent runs in progress may lose access.
          </p>
        </ConfirmModal>
      )}

      {/* Toast Notifications */}
      <IonToast
        isOpen={toast.show}
        message={toast.message}
        duration={3000}
        onDidDismiss={() => setToast({ show: false, message: '', color: '' })}
        cssClass="custom-toast"
      />
    </PageLayout>
    </>
  );
};

export default ProjectsPage;
